/**
 * Message Reliability Utilities
 * Ensures messages are never lost during thread creation and sending
 */

import { getInhouseAnonymousId } from "src/utils/anonymousId";

// Constants
const STORAGE_KEYS = {
  PENDING_MESSAGE: 'pendingThreadMessage',
  TRANSACTIONS: 'messageTransactions',
  UNSENT_MESSAGES: 'unsentMessages'
};

const EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours
const MAX_RETRY_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 1000;

/**
 * Persistent Storage Handler
 * Manages localStorage with error handling and expiry
 */
export const PersistentStorage = {
  savePendingMessage(message) {
    try {
      const data = {
        ...message,
        timestamp: message.timestamp || Date.now()
      };
      localStorage.setItem(STORAGE_KEYS.PENDING_MESSAGE, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Failed to save pending message:', error);
      return false;
    }
  },

  getPendingMessage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PENDING_MESSAGE);
      if (!stored) return null;

      const data = JSON.parse(stored);
      
      // Check expiry
      if (Date.now() - data.timestamp > EXPIRY_TIME) {
        this.clearPendingMessage();
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to get pending message:', error);
      return null;
    }
  },

  clearPendingMessage() {
    try {
      localStorage.removeItem(STORAGE_KEYS.PENDING_MESSAGE);
    } catch (error) {
      console.error('Failed to clear pending message:', error);
    }
  },

  updatePendingMessageThreadId(threadId) {
    try {
      const existing = this.getPendingMessage();
      if (!existing) return false;

      const data = {
        ...existing,
        threadId: threadId,
        threadIdUpdatedAt: Date.now()
      };
      localStorage.setItem(STORAGE_KEYS.PENDING_MESSAGE, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Failed to update pending message threadId:', error);
      return false;
    }
  },

  // Transaction storage
  saveTransactions(transactions) {
    try {
      localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify(transactions));
      return true;
    } catch (error) {
      console.error('Failed to save transactions:', error);
      return false;
    }
  },

  getTransactions() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.TRANSACTIONS);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to get transactions:', error);
      return {};
    }
  }
};

/**
 * Message Transaction Manager
 * Tracks the complete lifecycle of message sending
 */
export const MessageTransaction = {
  transactions: {},

  // Initialize from storage
  init() {
    this.transactions = PersistentStorage.getTransactions();
  },

  create(message, attachments = []) {
    const transaction = {
      id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      status: 'PENDING',
      message,
      attachments,
      attempts: [],
      createdAt: Date.now()
    };

    this.transactions[transaction.id] = transaction;
    this.persist();
    return transaction;
  },

  get(transactionId) {
    return this.transactions[transactionId] || null;
  },

  save(transaction) {
    this.transactions[transaction.id] = transaction;
    this.persist();
  },

  updateStatus(transactionId, status, data = {}) {
    const transaction = this.get(transactionId);
    if (!transaction) return;

    transaction.status = status;
    transaction.lastUpdatedAt = Date.now();
    
    if (data) {
      Object.assign(transaction, data);
    }

    // Add attempt record
    transaction.attempts.push({
      status,
      attemptedAt: Date.now(),
      ...data
    });

    this.save(transaction);
  },

  recordAttempt(transactionId, attemptData) {
    const transaction = this.get(transactionId);
    if (!transaction) return;

    transaction.attempts.push({
      ...attemptData,
      attemptedAt: attemptData.attemptedAt || Date.now()
    });

    this.save(transaction);
  },

  cleanup() {
    // Remove completed transactions
    Object.keys(this.transactions).forEach(id => {
      const transaction = this.transactions[id];
      if (transaction.status === 'COMPLETED') {
        delete this.transactions[id];
      }
    });
    this.persist();
  },

  persist() {
    PersistentStorage.saveTransactions(this.transactions);
  }
};

/**
 * Message Recovery Manager
 * Handles recovery of failed operations
 */
export const MessageRecovery = {
  checkPendingOperations() {
    const pendingMessage = PersistentStorage.getPendingMessage();
    const transactions = MessageTransaction.transactions;
    const incompleteTransactions = Object.values(transactions).filter(
      t => t.status !== 'COMPLETED'
    );

    return {
      hasPendingMessage: !!pendingMessage,
      pendingMessage,
      hasIncompleteTransactions: incompleteTransactions.length > 0,
      hasFailedTransactions: incompleteTransactions.some(t => 
        t.status === 'THREAD_CREATION_FAILED' || t.status === 'MESSAGE_SEND_FAILED'
      ),
      transactions: incompleteTransactions
    };
  },

  recoverTransaction(transactionId) {
    const transaction = MessageTransaction.get(transactionId);
    if (!transaction) return null;

    // Ensure attempts array exists
    if (!transaction.attempts) {
      transaction.attempts = [];
    }

    // Check max retries
    if (transaction.attempts.length >= MAX_RETRY_ATTEMPTS) {
      return {
        action: 'MANUAL_INTERVENTION_REQUIRED',
        reason: 'MAX_RETRIES_EXCEEDED',
        transaction
      };
    }

    // Determine recovery action based on status
    switch (transaction.status) {
      case 'THREAD_CREATION_FAILED':
        return {
          action: 'RETRY_THREAD_CREATION',
          data: transaction.message,
          attachments: transaction.attachments,
          transaction
        };

      case 'MESSAGE_SEND_FAILED':
        return {
          action: 'RETRY_MESSAGE_SEND',
          threadId: transaction.threadId,
          message: transaction.message,
          attachments: transaction.attachments,
          transaction
        };

      case 'THREAD_CREATING':
        // May have been interrupted
        return {
          action: 'CHECK_THREAD_STATUS',
          transaction
        };

      default:
        return {
          action: 'UNKNOWN',
          transaction
        };
    }
  },

  calculateBackoff(attemptCount) {
    // Exponential backoff with jitter
    const exponentialDelay = Math.pow(2, attemptCount) * BASE_BACKOFF_MS;
    const jitter = Math.random() * 0.3 * exponentialDelay; // 30% jitter
    return exponentialDelay + jitter;
  },

  shouldRetry(transaction) {
    if (!transaction) return false;
    if (transaction.attempts.length >= MAX_RETRY_ATTEMPTS) return false;
    
    // Check if enough time has passed since last attempt
    const lastAttempt = transaction.attempts[transaction.attempts.length - 1];
    if (!lastAttempt) return true;

    const timeSinceLastAttempt = Date.now() - lastAttempt.attemptedAt;
    const requiredBackoff = this.calculateBackoff(transaction.attempts.length - 1);
    
    return timeSinceLastAttempt >= requiredBackoff;
  }
};

// Initialize on module load
MessageTransaction.init();

// Export utility functions
export const createMessageTransaction = (message, attachments) => {
  return MessageTransaction.create(message, attachments);
};

export const updateTransactionStatus = (transactionId, status, data) => {
  MessageTransaction.updateStatus(transactionId, status, data);
};

export const checkForPendingOperations = () => {
  return MessageRecovery.checkPendingOperations();
};

export const recoverFailedTransaction = (transactionId) => {
  return MessageRecovery.recoverTransaction(transactionId);
};

export const clearCompletedTransactions = () => {
  MessageTransaction.cleanup();
};

const BASE_ENDPOINT = process.env.REACT_APP_BACKEND_BASE_ENDPOINT;
const POLL_MAX_ATTEMPTS = 5;
const POLL_BASE_BACKOFF_MS = 2000;

/**
 * Fetch the current processing status of a thread from the backend.
 * Returns null on any error so callers can handle gracefully.
 */
export const fetchThreadStatus = async (threadId, accessToken) => {
  try {
    let headers;
    if (typeof window !== "undefined" && window.location.pathname.includes("/try")) {
      const anonymousId = await getInhouseAnonymousId();
      headers = { "anonymous-user-id": anonymousId };
    } else {
      headers = { Authorization: `Bearer ${accessToken}` };
    }
    const response = await fetch(`${BASE_ENDPOINT}/api/thread/${threadId}/status`, {
      headers,
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

/**
 * Poll the thread status API with exponential backoff until the action is no
 * longer WAIT, or until max attempts are exhausted.
 * Returns the final status response, or null if all attempts failed/timed out.
 */
export const pollThreadStatus = async (threadId, accessToken) => {
  for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
    const status = await fetchThreadStatus(threadId, accessToken);
    if (!status) return null;
    if (status.action !== 'WAIT') return status;

    const delay = Math.pow(2, attempt) * POLL_BASE_BACKOFF_MS;
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
  return null;
};